import React from 'react';
import { Link } from 'react-router-dom';

const PatientCommunication = () => {
  const features = [
    {
      title: "Automated Appointment Reminders",
      description: "Smart, personalized reminders that adapt to patient preferences and behavior",
      icon: "⏰",
      benefits: [
        "95% reduction in no-shows",
        "Multi-channel communication",
        "Smart timing optimization"
      ]
    },
    {
      title: "Personalized Follow-ups",
      description: "AI-driven follow-up messages based on treatment type and patient history",
      icon: "📱",
      benefits: [
        "Improved patient satisfaction",
        "Better treatment adherence",
        "Enhanced patient engagement"
      ]
    },
    {
      title: "Treatment Plan Communication",
      description: "Clear, visual communication of treatment plans and progress",
      icon: "📋",
      benefits: [
        "Increased treatment acceptance",
        "Better patient understanding",
        "Reduced anxiety"
      ]
    },
    {
      title: "Smart Patient Education",
      description: "Personalized educational content delivered at the right time",
      icon: "📚",
      benefits: [
        "Improved oral health outcomes",
        "Better patient compliance",
        "Reduced treatment time"
      ]
    }
  ];

  const testimonials = [
    {
      quote: "The automated communication system has transformed how we interact with our patients. No-shows have decreased dramatically, and patient satisfaction is at an all-time high.",
      author: "Dr. Emily Chen",
      practice: "Bright Smile Dental"
    },
    {
      quote: "Our patients love the personalized follow-ups and educational content. It's made a huge difference in treatment compliance and outcomes.",
      author: "Dr. Michael Rodriguez",
      practice: "Family Dental Care"
    }
  ];

  return (
    <div className="pt-24 bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              Intelligent Patient Communication
            </h1>
            <p className="text-xl text-gray-600">
              Transform patient engagement with AI-powered communication tools that deliver personalized care at scale
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-[#1E2A4A] mb-8 text-center">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: 1,
                title: "Patient Profile Analysis",
                description: "AI analyzes patient history, preferences, and communication patterns"
              },
              {
                step: 2,
                title: "Smart Message Generation",
                description: "Personalized messages created based on patient profile and context"
              },
              {
                step: 3,
                title: "Automated Delivery",
                description: "Messages sent through preferred channels at optimal times"
              }
            ].map((step) => (
              <div 
                key={step.step}
                className="text-center p-6 bg-gray-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <div className="text-2xl text-orange-500 mb-4">"</div>
              <p className="text-gray-600 mb-6">
                {testimonial.quote}
              </p>
              <div>
                <div className="font-bold text-[#1E2A4A]">
                  {testimonial.author}
                </div>
                <div className="text-gray-600">
                  {testimonial.practice}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-orange-500 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Patient Communication?
          </h2>
          <p className="text-xl mb-8">
            Experience the power of AI-driven patient engagement
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/schedule-demo"
              className="bg-white text-orange-500 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Schedule a Demo
            </Link>
            <Link 
              to="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-orange-500 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientCommunication; 